import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import {
	SaveCrewDocument,
	DeleteCrewDocument,
	GetCrewDocument,
	GetCrewsDocument,
	CrewInput,
	CrewConversationsDocument,
	CrewsPerFlightDocument,
	PilotsPerFlightDocument,
	GetAllCrewForDialogDocument,
	OrderDirection,
} from '../../graphql/generated/gen-types';
import { AuthenticationService } from '../authentication/authentication.service';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { collection, Firestore, limit, onSnapshot, orderBy, query, where } from '@angular/fire/firestore';
import { TenantSettingService } from '../tenantSetting/tenant-setting.service';

@Injectable({
	providedIn: 'root',
})
export class CrewService {
	currentUser;

	constructor(
		private apollo: Apollo,
		private authenticationService: AuthenticationService,
		private firestore: Firestore,
		private tenantSettingService: TenantSettingService
	) {
		this.authenticationService.currentUser.subscribe((user) => {
			this.currentUser = user;
		});
	}

	public getList(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetCrewsDocument,
		});
	}

	public get(crewId: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetCrewDocument,
			variables: {
				crewId,
			},
		});
	}

	public getAllCrewForDialog(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetAllCrewForDialogDocument,
		});
	}

	public crewConversations(offset: number): QueryRef<any> {
		return this.apollo.watchQuery({
			query: CrewConversationsDocument,
			variables: {
				offset,
			},
		});
	}

	// Teardown matters here — same leaked-onSnapshot pattern as getBookingConversations (#165)
	public getCrewConversations(history: number): Observable<any[]> {
		return new Observable<any[]>((subscriber) => {
			subscriber.next([]);
			let snapshotUnsubscribe: (() => void) | null = null;
			let closed = false;
			const settingsSub = this.tenantSettingService
				.getTenantSetting()
				.valueChanges.pipe(take(1))
				.subscribe((result) => {
					let tenantId = result.data.tenantSetting?.tenantId;
					if (!tenantId) return;

					const collectionName = `/tenants/${tenantId}/crew/`;
					const constraints: any[] = [where('removed', '==', null)];
					if (history > 0) {
						constraints.push(orderBy('smsLatest.date', OrderDirection.Desc));
						constraints.push(limit(history));
					}
					const collection_ = collection(this.firestore, collectionName);
					const query_ = query(collection_, ...constraints);

					if (closed) return;
					snapshotUnsubscribe = onSnapshot(
						query_,
						(snapshot) => {
							subscriber.next(
								snapshot.docs
									.filter((doc) => doc.data().smsLatest)
									.map((doc) => {
										const crew = { ...doc.data(), smsType: 'CRE', id: doc.id } as any;
										if (crew.smsLatest?.date) {
											crew.smsLatest.date = crew.smsLatest.date.toDate();
											crew.smsLatest.summary =
												crew.smsLatest?.text?.length > 25 ? crew.smsLatest.text.substring(0, 25) + '...' : crew.smsLatest?.text;
										}
										return crew;
									}),
							);
						},
						(error) => {
							console.error('Error getting crew conversations', error);
						},
					);
				});
			return () => {
				closed = true;
				settingsSub.unsubscribe();
				if (snapshotUnsubscribe) snapshotUnsubscribe();
			};
		});
	}

	public save(crewInput: CrewInput, crewId: string): Observable<any> {
		// refetch crew list on save
		const refetchQueries: any[] = [
			{
				query: GetCrewsDocument,
			},
		];

		// refetch balloon document on update (not on new)
		if (crewId) {
			refetchQueries.push({
				query: GetCrewDocument,
				variables: {
					crewId,
				},
			});
		}

		return this.apollo
			.mutate<any>({
				mutation: SaveCrewDocument,
				variables: {
					crewInput,
					crewId,
				},
				refetchQueries,
			})
			.pipe(map((result: any) => result.data));
	}

	public delete(crewId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteCrewDocument,
				variables: {
					crewId,
				},
				refetchQueries: [
					{
						query: GetCrewsDocument,
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}
	public crewsPerFlight(flightId: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: CrewsPerFlightDocument,
			variables: {
				flightId,
			},
			fetchPolicy: 'no-cache',
		});
	}
	public pilotsPerFlight(flightId: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: PilotsPerFlightDocument,
			variables: {
				flightId,
			},
			fetchPolicy: 'no-cache',
		});
	}
}
